import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  Breadcrumbs,
  Link,
  Grid,
  Menu,
  MenuItem,
} from '@mui/material';
import { Visibility, Reply, Email, Phone, Business, ChatBubbleOutline, ContentCopy, Check, Settings, Delete, DeleteSweep, Groups, ArrowDropDown, RequestQuote } from '@mui/icons-material';
import { getRfqs, updateRfqStatus, deleteRfq, deleteRfqsBulk } from '../../services/api';

/* ── Design tokens ─────────────────────────────────────────────── */
const PRIMARY = '#6C98AD';
const PRIMARY_HOVER = '#5a8499';
const SECONDARY = '#24272a';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const CARD_SHADOW_HOVER = '0 8px 30px rgba(108, 152, 173, 0.25)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

/* ── Status metadata (single source of truth for label/colour per status) ─ */
const STATUS_OPTIONS = ['UNREAD', 'PENDING', 'PROCESSING', 'RESPONDED'];
const STATUS_META = {
  UNREAD: { label: 'Unread', bg: SECONDARY, color: '#fff' },
  PENDING: { label: 'Pending', bg: 'rgba(0,0,0,0.06)', color: '#888' },
  PROCESSING: { label: 'Processing', bg: 'rgba(230, 81, 0, 0.12)', color: '#e65100' },
  RESPONDED: { label: 'Responded', bg: 'rgba(46, 125, 50, 0.12)', color: '#2e7d32' },
};

export default function RfqsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('list');
  const [copiedField, setCopiedField] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuTargetId, setStatusMenuTargetId] = useState(null);

  const openStatusMenu = (e, id) => {
    e.stopPropagation();
    setStatusMenuAnchor(e.currentTarget);
    setStatusMenuTargetId(id);
  };

  const closeStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuTargetId(null);
  };

  const handleSelectRequest = (r) => {
    setSelected(r);
    setView('detail');
  };

  const handleBackToList = () => {
    setView('list');
    setSelected(null);
  };

  // Force back to the list view whenever "Request for Quote (RFQ)" is clicked in the sidebar, even if a request is currently open
  useEffect(() => {
    if (!location.state?.resetToList) return;
    handleBackToList();
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleCopy = async (value, fieldLabel) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldLabel);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelectedIds([]);
    try {
      const statusParam = filter === 'all' ? undefined : filter.toUpperCase();
      const res = await getRfqs({ page: page + 1, limit: rowsPerPage, status: statusParam });
      setRequests(res.data || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load RFQs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateStatus = async (id, status) => {
    try {
      await updateRfqStatus(id, status);
      setRequests(prev => prev.map(r => (r.id === id ? { ...r, status } : r)));
      setSelected(prev => (prev && prev.id === id ? { ...prev, status } : prev));
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      closeStatusMenu();
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRfq(id);
      setDeleteConfirm(null);
      handleBackToList();
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to delete RFQ');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await deleteRfqsBulk(selectedIds);
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to delete RFQs');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(requests.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const parseConfig = (raw) => {
    if (!raw) return null;
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return null;
    }
  };

  return (
    <Box>
      {view === 'detail' && selected && (
        <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
          <Link
            component="button"
            underline="hover"
            onClick={handleBackToList}
            sx={{
              fontFamily: FONT_BODY,
              fontWeight: 500,
              color: PRIMARY,
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              p: 0,
              '&:hover': { color: PRIMARY_HOVER },
            }}
          >
            Request for Quote (RFQ)
          </Link>
          <Typography sx={{ fontFamily: FONT_BODY, color: SECONDARY, fontWeight: 600 }}>
            {selected.name}
          </Typography>
        </Breadcrumbs>
      )}

      {view === 'list' && (
      <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: FONT_HEADING,
            fontWeight: 700,
            background: `linear-gradient(135deg, ${SECONDARY} 0%, ${PRIMARY} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Request for Quote (RFQ)
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={filter}
          exclusive
          onChange={(_, v) => { if (v) { setFilter(v); setPage(0); } }}
          sx={{
            gap: 1,
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              '&:not(:first-of-type)': { borderRadius: RADIUS, ml: 0 },
              '&:first-of-type': { borderRadius: RADIUS },
            },
            '& .MuiToggleButton-root': {
              fontFamily: FONT_BODY,
              textTransform: 'none',
              borderRadius: RADIUS,
              px: 2.5,
              py: 0.75,
              fontSize: '0.875rem',
              fontWeight: 500,
              border: `2px solid rgba(108, 152, 173, 0.2)`,
              color: SECONDARY,
              transition: TRANSITION,
              '&:hover': {
                backgroundColor: 'rgba(108, 152, 173, 0.08)',
                borderColor: PRIMARY,
              },
              '&.Mui-selected': {
                backgroundColor: SECONDARY,
                color: '#fff',
                borderColor: SECONDARY,
                boxShadow: '0 2px 8px rgba(36, 39, 42, 0.25)',
                '&:hover': {
                  backgroundColor: '#3d4348',
                  borderColor: '#3d4348',
                },
              },
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="unread">Unread</ToggleButton>
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="processing">Processing</ToggleButton>
          <ToggleButton value="responded">Responded</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Bulk Delete Bar */}
      {selectedIds.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            borderRadius: RADIUS,
            background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.08) 0%, rgba(211, 47, 47, 0.04) 100%)',
            border: '2px solid rgba(211, 47, 47, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: SECONDARY }}>
            {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
          </Typography>
          <Button
            variant="contained"
            startIcon={<DeleteSweep />}
            onClick={() => setBulkDeleteConfirm(true)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            Delete Selected
          </Button>
          <Button
            onClick={() => setSelectedIds([])}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 500,
              color: '#666',
            }}
          >
            Clear Selection
          </Button>
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      ) : requests.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: RADIUS }}>No RFQs found.</Alert>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: RADIUS,
            border: '2px solid rgba(108, 152, 173, 0.12)',
            boxShadow: CARD_SHADOW,
            overflow: 'hidden',
            transition: TRANSITION,
            '&:hover': { boxShadow: CARD_SHADOW_HOVER },
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: 'rgba(108, 152, 173, 0.05)' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.length > 0 && selectedIds.length < requests.length}
                      checked={requests.length > 0 && selectedIds.length === requests.length}
                      onChange={handleSelectAll}
                      sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY }, '&.MuiCheckbox-indeterminate': { color: PRIMARY } }}
                    />
                  </TableCell>
                  {['Status', 'Name', 'Company', 'Email', 'Date', ''].map((h, i) => (
                    <TableCell
                      key={h || 'actions'}
                      align={i === 5 ? 'right' : 'left'}
                      sx={{ fontFamily: FONT_HEADING, fontWeight: 600, fontSize: '0.8rem', color: SECONDARY, letterSpacing: 0.3 }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    selected={selectedIds.includes(r.id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: selectedIds.includes(r.id) ? 'rgba(108, 152, 173, 0.12)' : r.status === 'UNREAD' ? 'rgba(108, 152, 173, 0.06)' : 'transparent',
                      transition: TRANSITION,
                      '&:hover': { bgcolor: 'rgba(108, 152, 173, 0.07)' },
                    }}
                    onClick={() => handleSelectRequest(r)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(r.id)}
                        onChange={() => handleSelectOne(r.id)}
                        sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={(e) => openStatusMenu(e, r.id)}
                        endIcon={<ArrowDropDown fontSize="small" />}
                        sx={{
                          fontFamily: FONT_BODY,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          textTransform: 'none',
                          borderRadius: '8px',
                          minWidth: 0,
                          px: 1.25,
                          py: 0.25,
                          bgcolor: STATUS_META[r.status]?.bg,
                          color: STATUS_META[r.status]?.color,
                          '&:hover': { bgcolor: STATUS_META[r.status]?.bg, opacity: 0.85 },
                        }}
                      >
                        {STATUS_META[r.status]?.label || r.status}
                      </Button>
                    </TableCell>
                    <TableCell sx={{ fontWeight: r.status === 'UNREAD' ? 600 : 400, fontFamily: FONT_BODY, color: SECONDARY }}>
                      {r.name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONT_BODY, color: '#555' }}>{r.company || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_BODY, color: '#555' }}>{r.email}</TableCell>
                    <TableCell sx={{ fontFamily: FONT_BODY, color: '#888', fontSize: '0.82rem' }}>
                      {formatDate(r.createdAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleSelectRequest(r); }}
                          sx={{ color: PRIMARY, transition: TRANSITION, '&:hover': { transform: 'scale(1.15)' } }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirm(r); }}
                          sx={{ color: '#d32f2f', transition: TRANSITION, '&:hover': { transform: 'scale(1.15)' } }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            sx={{ fontFamily: FONT_BODY }}
          />
        </Paper>
      )}
      </>
      )}

      {view === 'detail' && selected && (
        <Box>
          {/* Header card: identity + status + actions */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: RADIUS,
              border: '2px solid rgba(108, 152, 173, 0.12)',
              boxShadow: CARD_SHADOW,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: SECONDARY }}>
                  {selected.name}
                </Typography>
                <Button
                  size="small"
                  onClick={(e) => openStatusMenu(e, selected.id)}
                  endIcon={<ArrowDropDown fontSize="small" />}
                  sx={{
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    borderRadius: '8px',
                    minWidth: 0,
                    px: 1.5,
                    py: 0.4,
                    bgcolor: STATUS_META[selected.status]?.bg,
                    color: STATUS_META[selected.status]?.color,
                    '&:hover': { bgcolor: STATUS_META[selected.status]?.bg, opacity: 0.85 },
                  }}
                >
                  {STATUS_META[selected.status]?.label || selected.status}
                </Button>
              </Box>
              <Typography variant="body2" sx={{ fontFamily: FONT_BODY, color: '#888' }}>
                RFQ • {formatDate(selected.createdAt)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Button
                component="a"
                href={`mailto:${encodeURIComponent(selected.email)}?subject=${encodeURIComponent(`Re: RFQ – Tailored Alloys`)}&body=${encodeURIComponent(`Hi ${selected.name},\n\n`)}`}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<Reply />}
                variant="outlined"
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontFamily: FONT_BODY,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 2.5,
                  py: 1,
                  borderColor: PRIMARY,
                  borderWidth: 2,
                  color: PRIMARY,
                  transition: TRANSITION,
                  '&:hover': {
                    borderColor: PRIMARY,
                    borderWidth: 2,
                    backgroundColor: PRIMARY,
                    color: '#fff',
                  },
                }}
              >
                Reply via Email
              </Button>
              <Button
                onClick={() => navigate('/admin/quotes', { state: { createFromRfqId: selected.id, rfqName: selected.name } })}
                variant="outlined"
                startIcon={<RequestQuote />}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontFamily: FONT_BODY,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 2.5,
                  py: 1,
                  borderColor: SECONDARY,
                  borderWidth: 2,
                  color: SECONDARY,
                  transition: TRANSITION,
                  '&:hover': {
                    borderColor: SECONDARY,
                    borderWidth: 2,
                    backgroundColor: SECONDARY,
                    color: '#fff',
                  },
                }}
              >
                Generate Quote
              </Button>
              <Button
                onClick={() => setDeleteConfirm(selected)}
                variant="outlined"
                startIcon={<Delete />}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontFamily: FONT_BODY,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 2.5,
                  py: 1,
                  borderColor: '#d32f2f',
                  borderWidth: 2,
                  color: '#d32f2f',
                  transition: TRANSITION,
                  '&:hover': {
                    borderColor: '#d32f2f',
                    borderWidth: 2,
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                  },
                }}
              >
                Delete
              </Button>
            </Box>
          </Paper>

          {/* Two-column content: main (message + configuration) / side (contact + pricing) */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {/* Message Section */}
              {selected.message && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: RADIUS,
                    border: '2px solid rgba(108, 152, 173, 0.12)',
                    boxShadow: CARD_SHADOW,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_HOVER} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ChatBubbleOutline sx={{ color: '#fff', fontSize: 18 }} />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: FONT_HEADING,
                        fontWeight: 600,
                        color: SECONDARY,
                      }}
                    >
                      Message
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(108, 152, 173, 0.06) 0%, rgba(108, 152, 173, 0.02) 100%)',
                      border: '1px solid rgba(108, 152, 173, 0.1)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: FONT_BODY,
                        color: SECONDARY,
                        lineHeight: 1.75,
                        fontSize: '1rem',
                      }}
                    >
                      {selected.message}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* Configuration details */}
              {(() => {
                const config = parseConfig(selected.configuration);
                if (!config || (Array.isArray(config) && config.length === 0)) return null;

                // Human-readable labels for configuration keys
                const keyLabels = {
                  'machine-model': 'Machine Model',
                  'laser-count': 'Lasers',
                  'scanner-model': 'Scanner Model',
                  'scanner-control-protocol': 'Control Protocol',
                  'flat-field-correction': 'Field Correction',
                  'lasers-to-monitor': 'Monitoring',
                  'sensors': 'Sensors',
                };

                // Keys considered "highlighted" (important at-a-glance info)
                const highlightKeys = new Set(['machine-model', 'laser-count', 'lasers-to-monitor']);

                // Column order for the table
                const columnOrder = ['machine-model', 'laser-count', 'lasers-to-monitor', 'scanner-model', 'scanner-control-protocol', 'flat-field-correction', 'sensors'];

                const formatValue = (key, val) => {
                  if (val === undefined || val === null || val === '') return '—';
                  if (Array.isArray(val)) return val.join(', ');
                  return String(val);
                };

                // Determine if this is multi-machine format or legacy flat format
                const isMultiMachine = config.machines && Array.isArray(config.machines);
                const machineList = isMultiMachine
                  ? config.machines
                  : [{ name: null, answers: config }];

                // Collect all keys present across machines for table columns
                const allKeys = [];
                const seenKeys = new Set();
                columnOrder.forEach((key) => {
                  machineList.forEach((m) => {
                    const answers = m.answers || m;
                    if (answers[key] !== undefined && answers[key] !== null && answers[key] !== '') {
                      if (!seenKeys.has(key)) {
                        seenKeys.add(key);
                        allKeys.push(key);
                      }
                    }
                  });
                });
                // Add any remaining keys not in columnOrder
                machineList.forEach((m) => {
                  const answers = m.answers || m;
                  Object.keys(answers).forEach((key) => {
                    if (!seenKeys.has(key) && answers[key] !== undefined && answers[key] !== null && answers[key] !== '' && key !== 'machines') {
                      seenKeys.add(key);
                      allKeys.push(key);
                    }
                  });
                });

                if (allKeys.length === 0) return null;

                return (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: RADIUS,
                      border: '2px solid rgba(108, 152, 173, 0.12)',
                      boxShadow: CARD_SHADOW,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2.5,
                        py: 1.5,
                        background: 'linear-gradient(135deg, rgba(108, 152, 173, 0.08) 0%, rgba(108, 152, 173, 0.03) 100%)',
                        borderBottom: '1px solid rgba(108, 152, 173, 0.12)',
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '7px',
                          background: `linear-gradient(135deg, ${SECONDARY} 0%, #3d4348 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Settings sx={{ color: '#fff', fontSize: 14 }} />
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontFamily: FONT_HEADING,
                          fontWeight: 600,
                          color: SECONDARY,
                        }}
                      >
                        Machine Configuration {isMultiMachine && machineList.length > 1 ? `(${machineList.length} machines)` : ''}
                      </Typography>
                    </Box>

                    {/* Per-machine field grid (avoids a wide, horizontally-scrolling table) */}
                    <Box sx={{ p: 2.5 }}>
                      {machineList.map((machine, idx) => {
                        const answers = machine.answers || machine;
                        return (
                          <Box
                            key={idx}
                            sx={{
                              pb: idx < machineList.length - 1 ? 2.5 : 0,
                              mb: idx < machineList.length - 1 ? 2.5 : 0,
                              borderBottom: idx < machineList.length - 1 ? '1px dashed rgba(108, 152, 173, 0.25)' : 'none',
                            }}
                          >
                            {isMultiMachine && machineList.length > 1 && (
                              <Typography
                                variant="subtitle2"
                                sx={{ fontFamily: FONT_HEADING, fontWeight: 700, color: PRIMARY, mb: 1.5 }}
                              >
                                {machine.name || `Machine ${idx + 1}`}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                                columnGap: 2,
                                rowGap: 1.75,
                              }}
                            >
                              {allKeys.map((key) => {
                                const val = formatValue(key, answers[key]);
                                const isHighlight = highlightKeys.has(key);
                                return (
                                  <Box key={key} sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        fontFamily: FONT_HEADING,
                                        textTransform: 'uppercase',
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                        color: '#999',
                                        letterSpacing: 0.5,
                                        mb: 0.25,
                                      }}
                                    >
                                      {keyLabels[key] || key.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: FONT_BODY,
                                        fontWeight: isHighlight ? 700 : 500,
                                        color: key === 'machine-model' ? PRIMARY : isHighlight ? SECONDARY : '#555',
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {val}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                );
              })()}
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Contact Info */}
              <Paper
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: RADIUS,
                  border: '2px solid rgba(108, 152, 173, 0.12)',
                  boxShadow: CARD_SHADOW,
                  overflow: 'hidden',
                }}
              >
                {[
                  { icon: <Email sx={{ fontSize: 20 }} />, label: 'Email', value: selected.email, copyable: true },
                  selected.phone && { icon: <Phone sx={{ fontSize: 20 }} />, label: 'Phone', value: selected.phone, copyable: true },
                  selected.company && { icon: <Business sx={{ fontSize: 20 }} />, label: 'Company', value: selected.company },
                  selected.americaMakesMember && { icon: <Groups sx={{ fontSize: 20 }} />, label: 'America Makes Member', value: selected.americaMakesMember === 'yes' ? 'Yes' : selected.americaMakesMember === 'no' ? 'No' : selected.americaMakesMember === 'planning' ? 'Not yet, but planning to' : selected.americaMakesMember },
                ].filter(Boolean).map((f, idx, arr) => {
                  const isCopied = copiedField === f.label;
                  const content = (
                    <Box
                      key={f.label}
                      onClick={f.copyable ? () => handleCopy(f.value, f.label) : undefined}
                      sx={{
                        p: 2.5,
                        borderBottom: idx < arr.length - 1 ? '1px solid rgba(108, 152, 173, 0.12)' : 'none',
                        transition: TRANSITION,
                        cursor: f.copyable ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: f.copyable ? 'rgba(108, 152, 173, 0.08)' : 'rgba(108, 152, 173, 0.03)',
                        },
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Box sx={{ color: isCopied ? '#4caf50' : PRIMARY, transition: TRANSITION }}>{f.icon}</Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#999',
                            fontFamily: FONT_HEADING,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            letterSpacing: 0.8,
                          }}
                        >
                          {f.label}
                        </Typography>
                        {f.copyable && (
                          <Box
                            sx={{
                              ml: 'auto',
                              color: isCopied ? '#4caf50' : '#bbb',
                              transition: TRANSITION,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {isCopied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: FONT_BODY,
                          color: isCopied ? '#4caf50' : SECONDARY,
                          fontWeight: 500,
                          fontSize: '0.95rem',
                          wordBreak: 'break-word',
                          transition: TRANSITION,
                        }}
                      >
                        {isCopied ? 'Copied!' : f.value}
                      </Typography>
                    </Box>
                  );

                  return f.copyable ? (
                    <Tooltip
                      key={f.label}
                      title={isCopied ? 'Copied!' : `Click to copy ${f.label.toLowerCase()}`}
                      arrow
                      placement="top"
                    >
                      {content}
                    </Tooltip>
                  ) : (
                    content
                  );
                })}
              </Paper>

              {/* Estimated Pricing */}
              {selected.estimatedPrice && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: RADIUS,
                    background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.08) 0%, rgba(46, 125, 50, 0.02) 100%)',
                    border: '1px solid rgba(46, 125, 50, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontSize: '14px', fontWeight: 700 }}>$</Typography>
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: FONT_HEADING,
                        fontWeight: 600,
                        color: '#2e7d32',
                      }}
                    >
                      Estimated Pricing
                    </Typography>
                  </Box>
                  {(() => {
                    const formatPrice = (price) => new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(price);

                    let breakdown = null;
                    try {
                      breakdown = selected.priceBreakdown
                        ? (typeof selected.priceBreakdown === 'string'
                          ? JSON.parse(selected.priceBreakdown)
                          : selected.priceBreakdown)
                        : null;
                    } catch {
                      breakdown = null;
                    }

                    return (
                      <Box>
                        {breakdown && breakdown.basePrice && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#555' }}>Base System (1 Laser)</Typography>
                            <Typography variant="body2" sx={{ color: '#555' }}>{formatPrice(breakdown.basePrice)}</Typography>
                          </Box>
                        )}
                        {breakdown && breakdown.additionalLasers > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#555' }}>Additional Lasers (×{breakdown.additionalLasers})</Typography>
                            <Typography variant="body2" sx={{ color: '#555' }}>{formatPrice(breakdown.additionalCost)}</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(46, 125, 50, 0.2)', pt: 1, mt: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#2e7d32' }}>Estimated Total</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#2e7d32' }}>{formatPrice(selected.estimatedPrice)}</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic', display: 'block', mt: 1 }}>
                          Prices exclude applicable taxes.
                        </Typography>
                      </Box>
                    );
                  })()}
                </Paper>
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Status dropdown menu, shared by the table rows and the detail header */}
      <Menu anchorEl={statusMenuAnchor} open={!!statusMenuAnchor} onClose={closeStatusMenu}>
        {STATUS_OPTIONS.map((s) => (
          <MenuItem key={s} onClick={() => handleUpdateStatus(statusMenuTargetId, s)}>
            {STATUS_META[s].label}
          </MenuItem>
        ))}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            boxShadow: '0 24px 48px rgba(36, 39, 42, 0.18)',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
          Delete RFQ
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT_BODY }}>
            Are you sure you want to delete this RFQ from <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteConfirm(null)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 500,
              color: '#666',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirm?.id)}
            variant="contained"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteConfirm}
        onClose={() => setBulkDeleteConfirm(false)}
        PaperProps={{
          sx: {
            borderRadius: '1rem',
            boxShadow: '0 24px 48px rgba(36, 39, 42, 0.18)',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 600, color: SECONDARY }}>
          Delete Multiple RFQs
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONT_BODY }}>
            Are you sure you want to delete <strong>{selectedIds.length}</strong> RFQ{selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setBulkDeleteConfirm(false)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 500,
              color: '#666',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              fontWeight: 600,
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
